import { z } from "zod";

/**
 * US-E16.F1-1021 (@UC0462) — `POST /v1/funds/:id/collections/receipt-scan` (RECEIPT-SPLIT S2, QĐ người dùng 22/10/2026).
 *
 * Quét ảnh hóa đơn ⇒ TỔNG để ĐIỀN SẴN ô «Tổng hóa đơn» khi tạo đợt thu một lần. Endpoint KHÔNG ghi gì về tiền (không đợt thu, không bút
 * toán, không lưu ảnh) — kết quả chỉ là gợi ý người dùng sửa được. Bộ đọc: `parseReceiptTotal` (`@goppy/domain`).
 *
 * 🔴 Tiền là STRING minor (CLAUDE.md §3.1): không number, không thập phân, không số 0 (tổng 0 đồng không phải kết quả), ≤12 chữ số (trùng trần
 * của bộ đọc). Không có trường «độ tin cậy» kiểu float — mức chắc chắn biểu diễn bằng CHÍNH nhánh `result`:
 *   - `resolved`  — một số rõ ⇒ điền sẵn (vẫn sửa được);
 *   - `ambiguous` — 1..N ứng viên ⇒ người dùng CHỌN (một ứng viên vẫn là «hỏi lại», không phải «chốt»: vd hóa đơn có đặt cọc / còn lại);
 *   - `not_found` — không điền gì, kèm lý do để app nói đúng câu (nhập tay).
 */

/** Trần số ứng viên — nhiều hơn ⇒ `not_found` `TOO_COMPLEX` (nhập tay), không vẽ một màn chọn dài. Review bảo mật S1 lượt 3. */
export const RECEIPT_SCAN_CANDIDATES_MAX = 5;

/** Tên quán tối đa (đếm theo KÝ TỰ — code point), trùng `RECEIPT_MERCHANT_NAME_MAX_LENGTH` của bộ đọc. */
export const RECEIPT_SCAN_MERCHANT_NAME_MAX = 80;

/**
 * Lý do `not_found` — tập ĐÓNG. Bốn lý do đầu do bộ đọc trả; `TOO_COMPLEX` do endpoint đặt (ảnh cho ra quá nhiều khối chữ / khối quá dài /
 * quá nhiều ứng viên — trần đặt TRƯỚC khi gọi bộ đọc).
 */
export const RECEIPT_SCAN_NOT_FOUND_REASONS = [
  "NO_TEXT",
  "NO_TOTAL_LINE",
  "FOREIGN_CURRENCY",
  "UNIT_SCALED",
  "TOO_COMPLEX",
] as const;
export type ReceiptScanNotFoundReason = (typeof RECEIPT_SCAN_NOT_FOUND_REASONS)[number];

/** Mã lỗi ổn định (client map `code → câu`). Đồng ý bên thứ ba dùng chung `AI_DATA_CONSENT_REQUIRED`. */
export const RECEIPT_SCAN_ERROR_CODES = Object.freeze({
  /** Cờ `ocr_collection_receipt` TẮT / vắng ⇒ 422, ảnh không tới nhà cung cấp. */
  DISABLED: "RECEIPT_SCAN_DISABLED",
  /** Quỹ không phải VND ⇒ 422, ảnh không tới nhà cung cấp (bộ đọc chỉ đọc VND, không quy đổi). */
  CURRENCY_UNSUPPORTED: "RECEIPT_SCAN_CURRENCY_UNSUPPORTED",
} as const);

const amountMinorSchema = z.string().regex(/^[1-9]\d{0,11}$/u, "số tiền minor dạng chuỗi, > 0, ≤ 12 chữ số");

const merchantNameSchema = z
  .string()
  .min(1)
  .refine((s) => Array.from(s).length <= RECEIPT_SCAN_MERCHANT_NAME_MAX, "tên quán quá dài")
  .nullable();

export const receiptScanResponseSchema = z.discriminatedUnion("result", [
  z
    .object({
      result: z.literal("resolved"),
      amountMinor: amountMinorSchema,
      currency: z.literal("VND"),
      merchantName: merchantNameSchema,
    })
    .strict(),
  z
    .object({
      result: z.literal("ambiguous"),
      candidatesMinor: z.array(amountMinorSchema).min(1).max(RECEIPT_SCAN_CANDIDATES_MAX),
      currency: z.literal("VND"),
      merchantName: merchantNameSchema,
    })
    .strict(),
  z
    .object({
      result: z.literal("not_found"),
      reason: z.enum(RECEIPT_SCAN_NOT_FOUND_REASONS),
      merchantName: merchantNameSchema,
    })
    .strict(),
]);

export type ReceiptScanResponse = z.infer<typeof receiptScanResponseSchema>;

/**
 * `GET /v1/funds/:id/collections/receipt-scan` — app hỏi TRƯỚC khi hiện nút «Quét hóa đơn» (S5): cờ `ocr_collection_receipt` mặc định TẮT và
 * chỉ đọc được qua cổng admin, nên thiếu bước hỏi này thì nút sẽ đưa người dùng qua màn đồng ý + chụp ảnh rồi mới bị từ chối (nút chết).
 * Cùng cổng với lượt quét (O/A/T · quỹ VND · cờ), KHÔNG gọi nhà cung cấp, KHÔNG xét đồng ý (app tự biết trạng thái đồng ý của mình).
 * `unavailableReason` dùng đúng mã lỗi của lượt quét — client map một bảng `code → câu`.
 */
export const receiptScanAvailabilitySchema = z.discriminatedUnion("available", [
  z.object({ available: z.literal(true), unavailableReason: z.null() }).strict(),
  z
    .object({
      available: z.literal(false),
      unavailableReason: z.enum([RECEIPT_SCAN_ERROR_CODES.DISABLED, RECEIPT_SCAN_ERROR_CODES.CURRENCY_UNSUPPORTED]),
    })
    .strict(),
]);

export type ReceiptScanAvailability = z.infer<typeof receiptScanAvailabilitySchema>;
